import { Card } from '#client/components/ui/Card';
import { Checkbox } from '#client/components/ui/Checkbox';
import { TextArea } from '#client/components/ui/TextArea';
import { formatMoney } from '#client/lib/format';
import {
  DemographicFields,
  type PatientMatch,
  ReturningPatientNotice,
} from '#client/features/patients';
import type { BookableLine, RegisterValues } from './register-request.logic';

interface RequestTestsCardProps {
  bookable: readonly BookableLine[];
  chosenTestIds: ReadonlySet<number>;
  unmatchedCount: number;
  onToggle: (testId: number, isChosen: boolean) => void;
}

export function RequestTestsCard({
  bookable,
  chosenTestIds,
  unmatchedCount,
  onToggle,
}: RequestTestsCardProps) {
  return (
    <Card title="Tests" subtitle="Only the ones matched to a catalogue entry can be booked">
      {bookable.length === 0 ? (
        <p className="c-field__help">
          None of the requested tests has been matched yet. Go back and match them first.
        </p>
      ) : (
        <ul className="c-order-request-lines">
          {bookable.map(({ lineId, requestedText, test }) => (
            <li key={lineId} className="c-order-request-line">
              <Checkbox
                label={`${test.name} — ${formatMoney(test.price)}`}
                checked={chosenTestIds.has(test.id)}
                onChange={(event) => onToggle(test.id, event.target.checked)}
              />
              <span className="c-muted">asked for as &ldquo;{requestedText}&rdquo;</span>
            </li>
          ))}
        </ul>
      )}

      {unmatchedCount > 0 && (
        <p className="c-field__help">
          {unmatchedCount} of the requested tests could not be matched and will not be booked. The
          request keeps what was written.
        </p>
      )}
    </Card>
  );
}

interface RegisterPatientCardProps {
  values: RegisterValues;
  onFieldsChange: (changes: Partial<RegisterValues>) => void;
  returning: PatientMatch | null;
}

export function RegisterPatientCard({
  values,
  onFieldsChange,
  returning,
}: RegisterPatientCardProps) {
  return (
    <Card title="The patient" subtitle="Fill in what you can confirm at the counter">
      {returning && <ReturningPatientNotice match={returning} />}
      <div className="c-form-grid">
        <DemographicFields values={values} onFieldsChange={onFieldsChange} errors={NO_ERRORS} />
      </div>

      <TextArea
        label="Note"
        rows={2}
        value={values.note}
        onChange={(event) => onFieldsChange({ note: event.target.value })}
      />

      <Checkbox
        label="Patient agrees to receive reports on WhatsApp"
        checked={values.whatsapp_consent}
        onChange={(event) => onFieldsChange({ whatsapp_consent: event.target.checked })}
      />
      <p className="c-field__help">
        Reports are sent automatically as a PDF to the contact number once every test is complete.
        WhatsApp is not a secure medical channel — anyone holding the phone can open the report.
      </p>
    </Card>
  );
}

/** Registration reports failures as one banner; the server's field errors are not mapped. */
export const NO_ERRORS: Partial<Record<string, string>> = {};
